import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { ReauthModal } from "./auth/ReauthModal";
import { RequireAuth } from "./auth/RequireAuth";
import { SessionProvider } from "./auth/SessionContext";
import "./index.css";
import { LoginPage } from "./pages/Login";
import { NotFoundPage } from "./pages/NotFound";
// ``Terminal.tsx`` from the v1 tree is the lone bridge component
// still mounted under the v2 shell — xterm + websocket lifecycle
// is non-trivial and a v2-native rebuild is deferred. Every other
// v1 page was deleted in v0.10.0; the v2 routes are now at the
// root (v0.12.0 hard URL flip).
import { TerminalPage } from "./pages/Terminal";
// v0.12.0 — v2 routes promoted to the root. No more ``/v2/`` in
// the URL bar. A single ``/v2/*`` catch-all (StripV2Prefix below)
// rewrites any stale bookmark or external link onto the new
// canonical paths. The v0.10.0 v1-retirement redirect block went
// away with the flip — its targets ARE the new canonical paths.
import { AppShell } from "./v2/layout/AppShell";
import { ThemeProvider } from "./v2/layout/ThemeProvider";
import { ToastProvider } from "./v2/components/Toast";
import { Home } from "./v2/pages/Home";
import { Help } from "./v2/pages/Help";
import {
  Locations,
  LocationDetail,
} from "./v2/pages/Locations";
import { ChassisDetailPage } from "./v2/pages/chassis/ChassisDetail";
import { VdslPortDetailPage } from "./v2/pages/chassis/VdslPortDetail";
import { OnuDetailPage } from "./v2/pages/chassis/OnuDetail";
import { OnuLookup } from "./v2/pages/tools/OnuLookup";
import { MacMap } from "./v2/pages/tools/MacMap";
import { Locks } from "./v2/pages/tools/Locks";
import {
  BulkJobs,
  BulkJobDetail,
} from "./v2/pages/tools/BulkJobs";
import {
  TerminalSessionsList,
  TerminalSessionDetail,
} from "./v2/pages/tools/TerminalSessionsList";
import { Jobs, JobDetail } from "./v2/pages/settings/Jobs";
import {
  Templates,
  TemplateDetail,
} from "./v2/pages/settings/Templates";
import {
  Schedules,
  ScheduleDetail,
} from "./v2/pages/settings/Schedules";
import { Audit } from "./v2/pages/settings/Audit";
import { AlarmRulesEditor } from "./v2/pages/settings/AlarmRulesEditor";
import { ApiKeys } from "./v2/pages/settings/ApiKeys";
import { Vpn } from "./v2/pages/settings/Vpn";
import { AccessControl } from "./v2/pages/settings/AccessControl";
import { MySettings } from "./v2/pages/settings/MySettings";
import { MobileShell } from "./v2/mobile/MobileShell";
import { MLocations } from "./v2/mobile/MLocations";
import { MLocationDevices } from "./v2/mobile/MLocationDevices";
import { MChassisPorts } from "./v2/mobile/MChassisPorts";
import { AllAlarms } from "./v2/pages/alarms/AllAlarms";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

// Tiny wrappers that hand URL params to components whose signature
// expects a typed prop rather than ``useParams`` inside.
function BulkJobDetailWrapper() {
  const { parentId } = useParams<{ parentId: string }>();
  return <BulkJobDetail parentId={parentId ?? ""} />;
}
function TerminalSessionDetailWrapper() {
  const { sid } = useParams<{ sid: string }>();
  return <TerminalSessionDetail sid={sid ?? ""} />;
}
function JobDetailWrapper() {
  const { jobId } = useParams<{ jobId: string }>();
  return <JobDetail jobId={jobId ?? ""} />;
}
function TemplateDetailWrapper() {
  const { templateId } = useParams<{ templateId: string }>();
  return <TemplateDetail templateId={templateId ?? ""} />;
}
function ScheduleDetailWrapper() {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  return <ScheduleDetail scheduleId={scheduleId ?? ""} />;
}

// ---------------------------------------------------------------------------
// /v2/* catch-all: strip the ``/v2`` prefix and redirect to the
// new canonical path. Exists so bookmarks + externally-shared
// links from the v0.11.x era still resolve. Bare ``/v2`` maps to
// ``/``. Query string + hash fragment preserved.
// ---------------------------------------------------------------------------
function StripV2Prefix() {
  const location = useLocation();
  const stripped = location.pathname.replace(/^\/v2/, "") || "/";
  return (
    <Navigate
      to={stripped + location.search + location.hash}
      replace
    />
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* /v2/* bookmark-compat — strip the prefix + redirect */}
            <Route path="/v2/*" element={<StripV2Prefix />} />
            {/* ---- v2 routes promoted to root (v0.12.0) ------------- */}
            <Route
              element={
                <RequireAuth>
                  <ThemeProvider>
                    <ToastProvider>
                      <>
                        <AppShell />
                        <ReauthModal />
                      </>
                    </ToastProvider>
                  </ThemeProvider>
                </RequireAuth>
              }
            >
              <Route path="/" element={<Home />} />
              <Route path="/help" element={<Help />} />
              <Route path="/locations" element={<Locations />} />
              <Route
                path="/locations/:locationId"
                element={<LocationDetail />}
              />
              <Route
                path="/chassis/:chassisId"
                element={<ChassisDetailPage />}
              />
              <Route
                path="/chassis/:chassisId/vdsl/:slot/:port"
                element={<VdslPortDetailPage />}
              />
              <Route
                path="/chassis/:chassisId/onu/:port/:displayId"
                element={<OnuDetailPage />}
              />
              <Route path="/tools/onu-lookup" element={<OnuLookup />} />
              <Route path="/tools/macs" element={<MacMap />} />
              <Route path="/tools/bulk-jobs" element={<BulkJobs />} />
              <Route
                path="/tools/bulk-jobs/:parentId"
                element={<BulkJobDetailWrapper />}
              />
              <Route
                path="/tools/terminal-sessions"
                element={<TerminalSessionsList />}
              />
              <Route
                path="/tools/terminal-sessions/:sid"
                element={<TerminalSessionDetailWrapper />}
              />
              <Route path="/tools/locks" element={<Locks />} />
              {/* Terminal — xterm + websocket lifecycle is non-trivial;
                  the v1 component is loaded inside the v2 shell so the
                  proven behaviour is preserved. A native-v2 rebuild
                  can swap this later. */}
              <Route
                path="/chassis/:chassisId/terminal"
                element={<TerminalPage />}
              />
              <Route path="/settings/jobs" element={<Jobs />} />
              <Route
                path="/settings/jobs/:jobId"
                element={<JobDetailWrapper />}
              />
              <Route path="/settings/templates" element={<Templates />} />
              <Route
                path="/settings/templates/:templateId"
                element={<TemplateDetailWrapper />}
              />
              <Route path="/settings/schedules" element={<Schedules />} />
              <Route
                path="/settings/schedules/:scheduleId"
                element={<ScheduleDetailWrapper />}
              />
              <Route path="/settings/audit" element={<Audit />} />
              <Route
                path="/settings/alarm-rules"
                element={<AlarmRulesEditor />}
              />
              <Route path="/settings/api-keys" element={<ApiKeys />} />
              <Route path="/settings/vpn" element={<Vpn />} />
              <Route path="/settings/access" element={<AccessControl />} />
              <Route path="/settings/me" element={<MySettings />} />
              <Route path="/alarms" element={<AllAlarms />} />
            </Route>
            {/* ---- /m — phone-first field-tech view (v0.32.0). Own
                minimal shell (no desktop Navbar); same session. ------ */}
            <Route
              element={
                <RequireAuth>
                  <ThemeProvider>
                    <ToastProvider>
                      <MobileShell />
                    </ToastProvider>
                  </ThemeProvider>
                </RequireAuth>
              }
            >
              <Route path="/m" element={<MLocations />} />
              <Route
                path="/m/locations/:locationId"
                element={<MLocationDevices />}
              />
              <Route
                path="/m/chassis/:chassisId"
                element={<MChassisPorts />}
              />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
